import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider
} from '@mui/material';
import { 
  TrendingDown, 
  TrendingUp, 
  Calendar, 
  Target, 
  Activity, 
  Pill,
  Ruler,
  Award,
  Clock,
  BarChart
} from 'lucide-react';
import { DatabaseSchema } from '../types';
import { startOfWeek, endOfWeek, subWeeks, differenceInDays } from 'date-fns';
import { getCurrentPhase, getDailyProteinTarget } from '../utils/nutritionPlan';

interface StatisticsProps {
  database: DatabaseSchema | null;
}

export const Statistics: React.FC<StatisticsProps> = ({ database }) => {
  if (!database) {
    return (
      <Box p={3}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Statistics
        </Typography>
        <Alert severity="info">Loading statistics...</Alert>
      </Box>
    );
  }

  const { weights, exercises, supplements, bodyMeasurements, meals } = database;

  // Weight statistics
  const getWeightStats = () => {
    if (weights.length === 0) return null;
    
    const sortedWeights = [...weights].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const latestWeight = sortedWeights[sortedWeights.length - 1];
    const startWeight = sortedWeights[0];
    const totalLoss = startWeight.weight - latestWeight.weight;
    const targetWeight = database.preferences?.goalWeight || database.goals?.targetWeight || 70;
    const remaining = latestWeight.weight - targetWeight;
    
    // Weekly progress
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const recentWeights = sortedWeights.filter(w => new Date(w.date) >= weekAgo);
    const weeklyLoss = recentWeights.length >= 2 ? 
      recentWeights[0].weight - recentWeights[recentWeights.length - 1].weight : 0;
    
    // Average BMI
    const validBMIs = sortedWeights.filter(w => w.bmi).map(w => w.bmi!);
    const avgBMI = validBMIs.length > 0 ? validBMIs.reduce((sum, bmi) => sum + bmi, 0) / validBMIs.length : null;
    
    return {
      current: latestWeight.weight,
      start: startWeight.weight,
      target: targetWeight,
      totalLoss,
      weeklyLoss,
      remaining,
      avgBMI,
      daysTracked: sortedWeights.length,
      timeSpan: differenceInDays(new Date(latestWeight.date), new Date(startWeight.date))
    };
  };

  // Exercise statistics
  const getExerciseStats = () => {
    const totalExercises = exercises.length;
    const completedExercises = exercises.filter(e => e.completed).length;
    const totalDuration = exercises.reduce((sum, e) => sum + e.duration, 0);
    const avgDuration = totalExercises > 0 ? totalDuration / totalExercises : 0;
    
    const typeStats = {
      walk: exercises.filter(e => e.type === 'walk').length,
      'gym-upper': exercises.filter(e => e.type === 'gym-upper').length,
      'gym-lower': exercises.filter(e => e.type === 'gym-lower').length,
    };
    
    // This week vs last week
    const thisWeekStart = startOfWeek(new Date());
    const lastWeekStart = startOfWeek(subWeeks(new Date(), 1));
    const lastWeekEnd = endOfWeek(subWeeks(new Date(), 1));
    
    const thisWeekExercises = exercises.filter(e => new Date(e.date) >= thisWeekStart);
    const lastWeekExercises = exercises.filter(e => {
      const date = new Date(e.date);
      return date >= lastWeekStart && date <= lastWeekEnd;
    });
    
    const thisWeekDuration = thisWeekExercises.reduce((sum, e) => sum + e.duration, 0);
    const lastWeekDuration = lastWeekExercises.reduce((sum, e) => sum + e.duration, 0);
    
    return {
      total: totalExercises,
      completed: completedExercises,
      completionRate: totalExercises > 0 ? (completedExercises / totalExercises) * 100 : 0,
      totalDuration,
      avgDuration,
      typeStats,
      thisWeekDuration,
      lastWeekDuration,
      weeklyChange: lastWeekDuration > 0 ? ((thisWeekDuration - lastWeekDuration) / lastWeekDuration) * 100 : 0
    };
  };

  // Supplement statistics
  const getSupplementStats = () => {
    const totalDays = supplements.length;
    if (totalDays === 0) return null;
    
    const adherenceStats: { [key: string]: { taken: number, total: number } } = {
      multivitamin: { taken: 0, total: totalDays },
      vitaminD3: { taken: 0, total: totalDays },
      omega3: { taken: 0, total: totalDays },
      orlistatLunch: { taken: 0, total: totalDays },
      orlistatDinner: { taken: 0, total: totalDays },
    };
    
    supplements.forEach(entry => {
      Object.keys(entry.supplements).forEach(key => {
        if (adherenceStats[key] && entry.supplements[key as keyof typeof entry.supplements]) {
          adherenceStats[key].taken++;
        }
      });
    });
    
    const totalSupplementsAvailable = Object.keys(adherenceStats).length;
    const overallAdherence = Object.values(adherenceStats).reduce((sum, stat) => sum + stat.taken, 0) / 
      (totalDays * totalSupplementsAvailable || 1) * 100;
    
    return { adherenceStats, overallAdherence, totalDays };
  };

  // Body measurement statistics
  const getBodyMeasurementStats = () => {
    if (bodyMeasurements.length === 0) return null;
    
    const sortedMeasurements = [...bodyMeasurements].sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    
    const latest = sortedMeasurements[sortedMeasurements.length - 1];
    const earliest = sortedMeasurements[0];
    
    const changes: { [key: string]: number } = {};
    let totalReduction = 0;
    let measurementCount = 0;
    
    Object.keys(latest.measurements).forEach(key => {
      const latestValue = latest.measurements[key as keyof typeof latest.measurements];
      const earliestValue = earliest.measurements[key as keyof typeof earliest.measurements];
      
      if (latestValue !== undefined && earliestValue !== undefined) {
        const change = earliestValue - latestValue;
        changes[key] = change;
        totalReduction += change;
        measurementCount++;
      }
    });
    
    return {
      totalEntries: bodyMeasurements.length,
      totalReduction,
      changes,
      timeSpan: differenceInDays(new Date(latest.date), new Date(earliest.date))
    };
  };

  // Meal statistics
  const getMealStats = () => {
    const totalMeals = meals.length;
    const totalCalories = meals.reduce((sum, m) => sum + (m.calories || 0), 0);
    const avgCalories = totalMeals > 0 ? totalCalories / totalMeals : 0;
    const totalProtein = meals.reduce((sum, m) => sum + (m.protein || 0), 0);
    const avgProtein = totalMeals > 0 ? totalProtein / totalMeals : 0;

    // Calculate daily protein averages
    const dateSet = new Set(meals.filter(m => m.protein).map(m => m.date));
    const daysWithProtein = dateSet.size;
    const avgDailyProtein = daysWithProtein > 0 ? totalProtein / daysWithProtein : 0;

    // Days meeting protein target (phase-based)
    const phase = getCurrentPhase(database?.preferences?.nutritionPhaseStartDate);
    const { min: proteinTargetMin } = getDailyProteinTarget(phase);
    const proteinByDate: Record<string, number> = {};
    meals.forEach(m => {
      if (m.protein) {
        proteinByDate[m.date] = (proteinByDate[m.date] || 0) + m.protein;
      }
    });
    const daysHittingTarget = Object.values(proteinByDate).filter(p => p >= proteinTargetMin).length;
    const proteinAdherence = daysWithProtein > 0 ? (daysHittingTarget / daysWithProtein) * 100 : 0;

    const mealTypes: Record<string, number> = {};
    meals.forEach(m => {
      mealTypes[m.mealType] = (mealTypes[m.mealType] || 0) + 1;
    });

    return { totalMeals, totalCalories, avgCalories, totalProtein, avgProtein, avgDailyProtein, daysWithProtein, daysHittingTarget, proteinAdherence, proteinTargetMin, mealTypes };
  };

  const weightStats = getWeightStats();
  const exerciseStats = getExerciseStats();
  const supplementStats = getSupplementStats();
  const bodyMeasurementStats = getBodyMeasurementStats();
  const mealStats = getMealStats();

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Statistics & Analytics
      </Typography>

      <Grid container spacing={3}>
        {/* Weight Loss Overview */}
        {weightStats && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Target size={24} color="#6366f1" />
                  <Typography variant="h6">Weight Loss Journey</Typography>
                </Box>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center" p={2}>
                      <Typography variant="h4" fontWeight="bold" color="primary">
                        {weightStats.totalLoss.toFixed(1)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        kg lost total
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center" p={2}>
                      <Typography variant="h4" fontWeight="bold" color="success.main">
                        {weightStats.remaining.toFixed(1)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        kg to target
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center" p={2}>
                      <Typography variant="h4" fontWeight="bold" color="warning.main">
                        {weightStats.weeklyLoss.toFixed(1)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        kg this week
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box textAlign="center" p={2}>
                      <Typography variant="h4" fontWeight="bold">
                        {weightStats.daysTracked}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        days tracked
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">Progress to Goal:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {((weightStats.start - weightStats.target) !== 0 ? (weightStats.totalLoss / (weightStats.start - weightStats.target)) * 100 : 0).toFixed(1)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, (weightStats.start - weightStats.target) !== 0 ? (weightStats.totalLoss / (weightStats.start - weightStats.target)) * 100 : 0)}
                      sx={{ mt: 1, height: 8, borderRadius: 4 }}
                    />
                  </Grid>
                  {weightStats.avgBMI && (
                    <Grid item xs={12} sm={4}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2">Average BMI:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {weightStats.avgBMI.toFixed(1)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={4}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">Time Span:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {weightStats.timeSpan} days
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Exercise Analytics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Activity size={24} color="#10b981" />
                <Typography variant="h6">Exercise Analytics</Typography>
              </Box>

              <Grid container spacing={2} mb={3}>
                <Grid item xs={6}>
                  <Box textAlign="center">
                    <Typography variant="h3" fontWeight="bold" color="primary">
                      {exerciseStats.total}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Workouts
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center">
                    <Typography variant="h3" fontWeight="bold" color="success.main">
                      {exerciseStats.completionRate.toFixed(0)}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Completion Rate
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Box mb={3}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2">Total Duration:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {Math.floor(exerciseStats.totalDuration / 60)}h {exerciseStats.totalDuration % 60}m
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2">Average Duration:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {exerciseStats.avgDuration.toFixed(0)} min
                  </Typography>
                </Box>
              </Box>

              <Typography variant="subtitle2" gutterBottom>Exercise Types:</Typography>
              {Object.entries(exerciseStats.typeStats).map(([type, count]) => (
                <Box key={type} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                    {type.replace('-', ' ')}:
                  </Typography>
                  <Chip size="small" label={count} />
                </Box>
              ))}

              <Divider sx={{ my: 2 }} />

              <Alert 
                severity={exerciseStats.weeklyChange >= 0 ? 'success' : 'warning'}
                sx={{ mt: 2 }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  {exerciseStats.weeklyChange >= 0 ? 
                    <TrendingUp size={16} /> : 
                    <TrendingDown size={16} />
                  }
                  <Typography variant="body2">
                    {exerciseStats.weeklyChange >= 0 ? 'Increased' : 'Decreased'} by{' '}
                    {Math.abs(exerciseStats.weeklyChange).toFixed(1)}% vs last week
                  </Typography>
                </Box>
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Supplement Adherence */}
        {supplementStats && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Pill size={24} color="#8b5cf6" />
                  <Typography variant="h6">Supplement Adherence</Typography>
                </Box>

                <Box textAlign="center" mb={3}>
                  <Typography variant="h3" fontWeight="bold" color="primary">
                    {supplementStats.overallAdherence.toFixed(0)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Overall Adherence
                  </Typography>
                </Box>

                <Typography variant="subtitle2" gutterBottom>Detailed Breakdown:</Typography>
                {Object.entries(supplementStats.adherenceStats).map(([supplement, stats]) => (
                  <Box key={supplement} mb={2}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {supplement.replace(/([A-Z])/g, ' $1').toLowerCase()}:
                      </Typography>
                      <Typography variant="body2">
                        {stats.taken}/{stats.total} ({((stats.taken / stats.total) * 100).toFixed(0)}%)
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={(stats.taken / stats.total) * 100}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                ))}

                <Box display="flex" alignItems="center" gap={1} mt={2}>
                  <Calendar size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Tracked for {supplementStats.totalDays} days
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Body Measurements */}
        {bodyMeasurementStats && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Ruler size={24} color="#f59e0b" />
                  <Typography variant="h6">Body Measurements</Typography>
                </Box>

                <Box textAlign="center" mb={3}>
                  <Typography variant="h3" fontWeight="bold" color="primary">
                    {bodyMeasurementStats.totalReduction.toFixed(1)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    cm total reduction
                  </Typography>
                </Box>

                <Typography variant="subtitle2" gutterBottom>Changes by Area:</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Area</TableCell>
                        <TableCell align="right">Change (cm)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(bodyMeasurementStats.changes).map(([area, change]) => (
                        <TableRow key={area}>
                          <TableCell sx={{ textTransform: 'capitalize' }}>
                            {area}
                          </TableCell>
                          <TableCell 
                            align="right"
                            sx={{ 
                              color: change > 0 ? 'success.main' : change < 0 ? 'error.main' : 'text.primary',
                              fontWeight: 'bold'
                            }}
                          >
                            {change > 0 ? '-' : '+'}{Math.abs(change).toFixed(1)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box display="flex" alignItems="center" gap={1} mt={2}>
                  <Clock size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Over {bodyMeasurementStats.timeSpan} days
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Meal Tracking */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <BarChart size={24} color="#6366f1" />
                <Typography variant="h6">Nutrition Overview</Typography>
              </Box>

              <Grid container spacing={2} mb={3}>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <Typography variant="h4" fontWeight="bold" color="primary">
                      {mealStats.totalMeals}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Meals Logged
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <Typography variant="h4" fontWeight="bold" color="#059669">
                      {mealStats.avgDailyProtein.toFixed(0)}g
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg Daily Protein
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <Typography variant="h4" fontWeight="bold" color="success.main">
                      {mealStats.proteinAdherence.toFixed(0)}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Protein Target Met
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Box mb={3}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Total Protein: {mealStats.totalProtein.toFixed(0)}g across {mealStats.daysWithProtein} days &middot;
                  Total Calories: {mealStats.totalCalories.toLocaleString()} &middot;
                  Avg Calories/Meal: {mealStats.avgCalories.toFixed(0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Days hitting {mealStats.proteinTargetMin}g+ protein: {mealStats.daysHittingTarget} / {mealStats.daysWithProtein}
                </Typography>
              </Box>

              <Typography variant="subtitle2" gutterBottom>Meal Slot Distribution:</Typography>
              {Object.entries(mealStats.mealTypes).map(([type, count]) => (
                <Box key={type} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                    {type.replace(/-/g, ' ')}:
                  </Typography>
                  <Chip size="small" label={count} />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Achievement Summary */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Award size={24} color="#f59e0b" />
                <Typography variant="h6">Achievement Summary</Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center" p={2} sx={{ backgroundColor: 'rgba(99, 102, 241, 0.1)', borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight="bold" color="primary">
                      {weights.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Weight Check-ins
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center" p={2} sx={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight="bold" color="success.main">
                      {exerciseStats.completed}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Workouts Completed
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center" p={2} sx={{ backgroundColor: 'rgba(139, 92, 246, 0.1)', borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight="bold" color="secondary">
                      {supplements.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Supplement Days
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center" p={2} sx={{ backgroundColor: 'rgba(245, 158, 11, 0.1)', borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight="bold" color="warning.main">
                      {bodyMeasurements.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Measurements
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};